import React, { useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import TaskAssignmentForm from "../../components/maintenance/TaskAssignmentForm";
import {
  fetchTaskAssignment,
  fetchTaskAssignmentSuccess,
  fetchTaskAssignmentFailure,
  createTaskAssignment,
  createTaskAssignmentSuccess,
  createTaskAssignmentFailure,
  updateTaskAssignment,
  updateTaskAssignmentSuccess,
  updateTaskAssignmentFailure,
  fetchTaskTemplates,
  fetchTaskTemplatesSuccess,
  fetchTaskTemplatesFailure,
  fetchMachines,
  fetchMachinesSuccess,
  fetchMachinesFailure,
  fetchFacilities,
  fetchFacilitiesSuccess,
  fetchFacilitiesFailure,
  clearCurrentTaskAssignment,
} from "../../store/maintenance/actions";

const TaskAssignmentFormContainer = ({
  assignment,
  templates,
  machines,
  facilities,
  loading,
  error,
  fetchTaskAssignment,
  createTaskAssignment,
  updateTaskAssignment,
  fetchTaskTemplates,
  fetchMachines,
  fetchFacilities,
  clearCurrentTaskAssignment,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  useEffect(() => {
    fetchTaskTemplates(1, null, null, null, true);
    fetchMachines();
    fetchFacilities();

    if (isEdit) {
      fetchTaskAssignment(id);
    }

    return () => {
      clearCurrentTaskAssignment();
    };
  }, [id, isEdit, fetchTaskAssignment, fetchTaskTemplates, fetchMachines, fetchFacilities, clearCurrentTaskAssignment]);

  const handleSubmit = useCallback(
    async (data) => {
      try {
        if (isEdit) {
          await updateTaskAssignment(id, data);
        } else {
          await createTaskAssignment(data);
        }
        navigate("/maintenance/task-assignments");
      } catch (err) {
        console.error("Error saving assignment:", err);
      }
    },
    [isEdit, id, createTaskAssignment, updateTaskAssignment, navigate]
  );

  return (
    <TaskAssignmentForm
      assignment={assignment}
      templates={templates}
      machines={machines}
      facilities={facilities}
      loading={loading}
      error={error}
      isEdit={isEdit}
      onSubmit={handleSubmit}
    />
  );
};

const mapStateToProps = (state) => ({
  assignment: state.maintenance.taskAssignments.current,
  templates: state.maintenance.taskTemplates.list,
  machines: state.maintenance.machines.list,
  facilities: state.maintenance.facilities.list,
  loading: state.maintenance.taskAssignments.loading,
  error: state.maintenance.taskAssignments.error,
});

const mapDispatchToProps = (dispatch) => ({
  fetchTaskAssignment: (id) => {
    dispatch(fetchTaskAssignment(id)).payload.then(
      (response) => {
        dispatch(fetchTaskAssignmentSuccess(response.data));
      },
      (error) => {
        dispatch(fetchTaskAssignmentFailure(error.response?.data || error));
      }
    );
  },
  createTaskAssignment: (data) => {
    return dispatch(createTaskAssignment(data)).payload.then(
      (response) => {
        dispatch(createTaskAssignmentSuccess(response.data));
      },
      (error) => {
        dispatch(createTaskAssignmentFailure(error.response?.data || error));
        throw error;
      }
    );
  },
  updateTaskAssignment: (id, data) => {
    return dispatch(updateTaskAssignment(id, data)).payload.then(
      (response) => {
        dispatch(updateTaskAssignmentSuccess(response.data));
      },
      (error) => {
        dispatch(updateTaskAssignmentFailure(error.response?.data || error));
        throw error;
      }
    );
  },
  fetchTaskTemplates: (page, taskType, targetType, frequency, isActive) => {
    dispatch(fetchTaskTemplates(page, taskType, targetType, frequency, isActive, null, 9999)).payload.then(
      (response) => {
        dispatch(fetchTaskTemplatesSuccess(response.data));
      },
      (error) => {
        dispatch(fetchTaskTemplatesFailure(error.response?.data || error));
      }
    );
  },
  fetchMachines: () => {
    dispatch(fetchMachines(1, null, null, null, null, 9999)).payload.then(
      (response) => {
        dispatch(fetchMachinesSuccess(response.data));
      },
      (error) => {
        dispatch(fetchMachinesFailure(error.response?.data || error));
      }
    );
  },
  fetchFacilities: () => {
    dispatch(fetchFacilities(1, null, null, null, null, 9999)).payload.then(
      (response) => {
        dispatch(fetchFacilitiesSuccess(response.data));
      },
      (error) => {
        dispatch(fetchFacilitiesFailure(error.response?.data || error));
      }
    );
  },
  clearCurrentTaskAssignment: () => dispatch(clearCurrentTaskAssignment()),
});

export default connect(mapStateToProps, mapDispatchToProps)(TaskAssignmentFormContainer);
